import { Link } from "react-router-dom";
import { Smartphone, Mail, Trash2, ShieldCheck, Clock, Database, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/data/site";

const IN_APP_STEPS = [
  {
    title: "Open Kinect Nova AI",
    desc: "Launch the app on your iPhone or Android device and make sure you're signed in to the account you want to delete.",
  },
  {
    title: "Go to Settings",
    desc: "Tap your profile picture in the top-right corner, then select Settings from the menu.",
  },
  {
    title: "Open the Account section",
    desc: "In Settings, tap Account to view your account and security options.",
  },
  {
    title: "Tap “Delete account”",
    desc: "Scroll to the bottom and tap Delete account. You may be asked to re-enter your password to confirm it's really you.",
  },
  {
    title: "Confirm deletion",
    desc: "Read the confirmation notice and tap Delete permanently. Your account and personal data will be scheduled for permanent deletion.",
  },
];

const WHAT_HAPPENS = [
  {
    icon: Trash2,
    title: "Everything is removed",
    desc: "Your profile, Medical Passport, health records, medications, messages and Emergency Circle data are permanently deleted.",
  },
  {
    icon: Clock,
    title: "Completed within 30 days",
    desc: "Deletion is completed within 30 days. We'll send a confirmation email once your data has been fully removed.",
  },
  {
    icon: Database,
    title: "Limited legal retention",
    desc: "Certain records may be retained only where strictly required by law (e.g. billing records), then securely deleted.",
  },
  {
    icon: ShieldCheck,
    title: "Cannot be undone",
    desc: "Account deletion is permanent. To use Kinect Nova AI again afterwards, you'll need to create a new account.",
  },
];

export default function DeleteAccount() {
  return (
    <>
      <Seo
        title="How to Delete Your Account"
        description="Step-by-step instructions to delete your Kinect Nova AI account and all associated personal data — directly in the app or by request. GDPR and CCPA compliant."
        path="/delete-account"
      />
      <PageHeader
        eyebrow="Account & privacy"
        title="How to delete your account"
        subtitle="You're always in control of your data. Follow these simple steps to permanently delete your Kinect Nova AI account, or send us a request and we'll handle it for you."
      />

      <section className="mx-auto max-w-4xl px-5 py-16 sm:px-8 lg:py-24">
        {/* In-app method */}
        <Reveal>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Smartphone className="h-5 w-5" />
            </div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              Option 1 — Delete in the app
            </h2>
          </div>
          <p className="mt-3 text-base text-muted-foreground">
            The fastest way. Recommended for most users.
          </p>
        </Reveal>

        <div className="relative mt-10">
          <div className="absolute left-[23px] top-4 hidden h-[calc(100%-2rem)] w-px bg-border sm:block" />
          <ol className="space-y-8">
            {IN_APP_STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.06} as="li" className="relative flex gap-5">
                <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary font-heading text-lg font-bold text-primary-foreground shadow-md">
                  {i + 1}
                </div>
                <div className="pt-1">
                  <h3 className="font-heading text-lg font-semibold text-foreground">{step.title}</h3>
                  <p className="mt-1 text-base leading-relaxed text-muted-foreground">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>

        {/* Request method */}
        <Reveal className="mt-16">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Mail className="h-5 w-5" />
            </div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              Option 2 — Request deletion
            </h2>
          </div>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Can't access the app, or prefer we handle it? Submit a deletion request through our
            secure form, or email us at{" "}
            <a href={`mailto:${SITE.supportEmail}`} className="font-medium text-primary hover:underline">
              {SITE.supportEmail}
            </a>{" "}
            from the email address on your account. We'll verify your identity and delete your data.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/data-deletion"
              data-testid="delete-account-request-btn"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 hover:-translate-y-0.5"
            >
              Open the deletion request form <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`mailto:${SITE.supportEmail}?subject=Account%20deletion%20request`}
              data-testid="delete-account-email-btn"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground transition-all hover:-translate-y-0.5 hover:bg-muted"
            >
              <Mail className="h-4 w-4" /> Email us
            </a>
          </div>
        </Reveal>

        {/* What happens */}
        <Reveal className="mt-16">
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            What happens to your data
          </h2>
        </Reveal>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {WHAT_HAPPENS.map((item, i) => {
            const Icon = item.icon;
            return (
              <Reveal
                key={item.title}
                delay={i * 0.05}
                className="rounded-3xl border border-border bg-card p-6"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">{item.title}</h3>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">{item.desc}</p>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-12 rounded-2xl border border-border bg-secondary/40 p-6">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Need more detail on how we handle your information? Read our{" "}
            <Link to="/privacy" className="font-medium text-primary hover:underline">Privacy Policy</Link>. For any
            questions about deleting your account, contact us at{" "}
            <a href={`mailto:${SITE.supportEmail}`} className="font-medium text-primary hover:underline">
              {SITE.supportEmail}
            </a>.
          </p>
        </Reveal>
      </section>
    </>
  );
}
